import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

export const Hero = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const lines = [
    { text: "Your Trusted", style: "regular" },
    { text: "Quality Assurance", style: "italic" },
    { text: "Partner", style: "regular" }
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#1a1a1a] via-[#2d1f1f] to-[#1a1a1a]">
      {/* Background Image with Parallax */}
      <motion.div 
        style={{ y }}
        className="absolute inset-0 z-0"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#8B1538]/40 via-transparent to-[#1a1a1a]/80 z-10" />
        <img
          src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHx0ZWNoJTIwdGVhbXxlbnwwfHx8fDE3ODQ3MzAwNzV8MA&ixlib=rb-4.1.0&q=85"
          alt="Quality Assurance Team"
          className="w-full h-full object-cover opacity-30"
        />
      </motion.div>

      {/* Grain Texture Overlay */}
      <div className="absolute inset-0 z-10 opacity-30 mix-blend-overlay"
        style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"%3E%3Cfilter id="noiseFilter"%3E%3CfeTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="4" stitchTiles="stitch"/%3E%3C/filter%3E%3Crect width="100%25" height="100%25" filter="url(%23noiseFilter)"/%3E%3C/svg%3E")',
        }}
      />

      <motion.div 
        style={{ opacity }}
        className="relative z-20 max-w-6xl mx-auto px-6 text-center"
      >
        {/* Elegant eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isLoaded ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center justify-center gap-3 mb-8"
        >
          <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#D4AF37]" />
          <span className="text-[#D4AF37] text-xs md:text-sm tracking-[0.3em] uppercase font-light">
            INDOAXIUM Software Services
          </span>
          <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#D4AF37]" />
        </motion.div>

        {/* Kinetic Title with Line-by-Line Reveal */}
        <div className="mb-8">
          {lines.map((line, index) => (
            <div key={index} className="overflow-hidden">
              <motion.h1
                initial={{ y: 60, opacity: 0 }}
                animate={isLoaded ? { y: 0, opacity: 1 } : {}}
                transition={{
                  duration: 0.9,
                  delay: 0.2 + index * 0.18,
                  ease: [0.22, 1, 0.36, 1]
                }}
                className={`text-3xl md:text-5xl lg:text-6xl leading-[1.1] ${
                  line.style === 'italic' ? 'italic font-normal' : 'font-medium'
                }`}
                style={{
                  fontFamily: "'Playfair Display', serif",
                  background: 'linear-gradient(135deg, #F4E4B0 0%, #D4AF37 50%, #B8941F 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  letterSpacing: line.style === 'italic' ? '0.01em' : '-0.02em',
                }}
              >
                {line.text}
              </motion.h1>
            </div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={isLoaded ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto font-light tracking-wide"
        >
          End-to-end software testing services that improve product quality,
          <br className="hidden md:block" />
          accelerate releases, and reduce business risks.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isLoaded ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <motion.a
            href="#services"
            whileHover={{ scale: 1.05, borderColor: '#D4AF37' }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-4 border-2 border-gray-600 text-[#D4AF37] rounded-sm font-semibold text-lg tracking-wide hover:border-[#D4AF37] transition-all duration-300"
          >
            Explore Services
          </motion.a>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isLoaded ? { opacity: 1, y: [0, 10, 0] } : {}}
          transition={{ 
            opacity: { duration: 0.8, delay: 1.2 },
            y: { duration: 2, repeat: Infinity, ease: "easeInOut" }
          }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2"
        >
          <ChevronDown className="w-8 h-8 text-[#D4AF37]" />
        </motion.div>
      </motion.div>
    </section>
  );
};